import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.qrmenu.rw/api';

interface Promotion {
    id: number;
    name: string;
    description: string;
    image_url?: string | null;
    image_alt_text?: string | null;
    type: 'percentage' | 'fixed_amount';
    value: number;
    applies_to: 'product' | 'category' | 'all';
    bg_color?: string;
    text_color?: string;
    products?: { id: number; name: string }[];
    categories?: { id: number; name: string }[];
}

interface PromotionBannerProps {
    establishmentId: string | number;
    onPromotionClick?: (promotion: Promotion) => void;
}

const PromotionBanner: React.FC<PromotionBannerProps> = ({ establishmentId, onPromotionClick }) => {
    const [promotions, setPromotions] = useState<Promotion[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [autoRotate, setAutoRotate] = useState(true);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        fetchActivePromotions();
    }, [establishmentId]);

    useEffect(() => {
        if (promotions.length <= 1 || !autoRotate || !isVisible) return;
        const interval = setInterval(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentIndex((prev) => (prev + 1) % promotions.length);
                setIsTransitioning(false);
                setImageError(false);
            }, 150);
        }, 5000);
        return () => clearInterval(interval);
    }, [promotions.length, autoRotate, isVisible]);

    const fetchActivePromotions = async () => {
        try {
            const response = await fetch(
                `${API_URL}/public/${establishmentId}/promotions/active`
            );
            if (response.ok) {
                const data = await response.json();
                setPromotions(data);
            }
        } catch (error) {
            console.error('Error fetching promotions:', error);
        }
    };

    const nextPromotion = () => {
        setAutoRotate(false);
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % promotions.length);
            setIsTransitioning(false);
            setImageError(false);
        }, 150);
    };

    const prevPromotion = () => {
        setAutoRotate(false);
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev - 1 + promotions.length) % promotions.length);
            setIsTransitioning(false);
            setImageError(false);
        }, 150);
    };

    const handleClose = () => {
        setIsVisible(false);
        sessionStorage.setItem(`promos_dismissed_${establishmentId}`, 'true');
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        if (distance > 50) nextPromotion();
        else if (distance < -50) prevPromotion();
        setTouchStart(0);
        setTouchEnd(0);
    };

    if (
        promotions.length === 0 ||
        !isVisible ||
        sessionStorage.getItem(`promos_dismissed_${establishmentId}`)
    ) {
        return null;
    }

    const currentPromo = promotions[currentIndex];
    const hasImage = currentPromo.image_url && !imageError;
    const bgColor = currentPromo.bg_color || '#8B0000';
    const textColor = currentPromo.text_color || '#FFFFFF';

    const getImageUrl = (path: string | null | undefined) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return `${API_URL}/uploads/${path}`;
    };

    /* ---------- styles (all inline, no Tailwind) ---------- */

    const wrapperStyle: React.CSSProperties = {
        position: 'sticky',
        top: 0,
        zIndex: 50,
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    };

    const bannerStyle: React.CSSProperties = {
        position: 'relative',
        overflow: 'hidden',
        height: 120,
        backgroundColor: hasImage ? '#000' : bgColor,
    };

    const overlayStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1,
    };

    const imgStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0, left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        zIndex: 0,
    };

    const contentStyle: React.CSSProperties = {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '100%',
        padding: '0 16px',
        opacity: isTransitioning ? 0.4 : 1,
        transition: 'opacity 0.15s ease',
    };

    const textBlockStyle: React.CSSProperties = {
        flex: 1,
        textAlign: 'center',
        minWidth: 0,
        cursor: onPromotionClick ? 'pointer' : 'default',
    };

    const nameStyle: React.CSSProperties = {
        margin: 0,
        fontSize: 20,
        fontWeight: 700,
        color: hasImage ? '#FFFFFF' : textColor,
        textShadow: hasImage ? '0 1px 4px rgba(0,0,0,0.8)' : 'none',
        letterSpacing: '0.5px',
    };

    const badgeStyle: React.CSSProperties = {
        display: 'inline-block',
        marginTop: 4,
        padding: '3px 10px',
        backgroundColor: '#EAB308',
        color: '#000',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 700,
    };

    const descStyle: React.CSSProperties = {
        margin: '4px 0 0',
        fontSize: 13,
        color: hasImage ? 'rgba(255,255,255,0.85)' : textColor,
        opacity: 0.9,
        textShadow: hasImage ? '0 1px 3px rgba(0,0,0,0.6)' : 'none',
    };

    const closeBtnStyle: React.CSSProperties = {
        flexShrink: 0,
        width: 32,
        height: 32,
        border: 'none',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.15)',
        color: hasImage ? '#fff' : textColor,
        fontSize: 18,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 8,
    };

    const dotsContainerStyle: React.CSSProperties = {
        position: 'absolute',
        bottom: 6,
        left: 0, right: 0,
        display: 'flex',
        justifyContent: 'center',
        gap: 6,
        zIndex: 3,
    };

    const dotStyle = (active: boolean): React.CSSProperties => ({
        width: active ? 24 : 8,
        height: 8,
        borderRadius: 4,
        border: 'none',
        backgroundColor: active ? '#EAB308' : 'rgba(255,255,255,0.45)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        padding: 0,
    });

    /* ---------- render ---------- */

    return (
        <div style={wrapperStyle}>
            <div
                style={bannerStyle}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Image background */}
                {hasImage && (
                    <>
                        <img
                            src={getImageUrl(currentPromo.image_url) || ''}
                            alt={currentPromo.image_alt_text || currentPromo.name}
                            style={imgStyle}
                            onError={() => setImageError(true)}
                        />
                        <div style={overlayStyle} />
                    </>
                )}

                {/* Content row */}
                <div style={contentStyle}>
                    <div
                        style={textBlockStyle}
                        onClick={() => onPromotionClick && onPromotionClick(currentPromo)}
                        role={onPromotionClick ? 'button' : undefined}
                        tabIndex={onPromotionClick ? 0 : undefined}
                        onKeyDown={(e) => {
                            if (onPromotionClick && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                onPromotionClick(currentPromo);
                            }
                        }}
                    >
                        <h3 style={nameStyle}>{currentPromo.name}</h3>
                        <div>
                            <span style={badgeStyle}>
                                {currentPromo.type === 'percentage'
                                    ? `${currentPromo.value}% OFF`
                                    : `${currentPromo.value} RWF OFF`}
                            </span>
                        </div>
                        {currentPromo.description && (
                            <p style={descStyle}>{currentPromo.description}</p>
                        )}
                        {onPromotionClick && currentPromo.applies_to !== 'all' && (
                            <span style={{
                                fontSize: 11,
                                color: hasImage ? 'rgba(255,255,255,0.7)' : textColor,
                                opacity: 0.8,
                                marginTop: 2,
                                display: 'inline-block',
                            }}>
                                Tap to view offer →
                            </span>
                        )}
                    </div>

                    <button onClick={handleClose} style={closeBtnStyle} aria-label="Close banner">
                        ✕
                    </button>
                </div>

                {/* Dot indicators */}
                {promotions.length > 1 && (
                    <div style={dotsContainerStyle}>
                        {promotions.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    setAutoRotate(false);
                                    setIsTransitioning(true);
                                    setTimeout(() => {
                                        setCurrentIndex(index);
                                        setIsTransitioning(false);
                                        setImageError(false);
                                    }, 150);
                                }}
                                style={dotStyle(index === currentIndex)}
                                aria-label={`Go to promotion ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PromotionBanner;
